import type { FunctionComponent, ReactNode } from 'react';
import { useState } from 'react';

import { AgentAttributions } from '~/feature/agents';
import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { Collapsible } from '~/ui/core/collapsible/Collapsible';
import { IconChevronRightSmall, IconCopy, IconFileDownload } from '~/ui/core/icons';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/ui/core/tabs/Tabs';

import { ChatMarkdown } from '../../../chat/ChatMarkdown';
import { useCopyTextToClipboard } from '../../../comments/use-copy-text-to-clipboard';
import type { WorkTaskAutomation } from '../../../data-contract/task-management';
import { useUrlTileEscapeClose } from '../../../url-tiles/UrlTileFocusScope';
import {
    findRecommendedAutomation,
    useAutomationRecommendationsEnabled,
    useWorkTaskAutomationRecommendation,
} from '../api/use-work-task-automation-recommendation';
import { profileTaskTranslationKeys } from '../profile-task-translations';
import { ProfileTaskBreadcrumbs } from '../tile/ProfileTaskBreadcrumbs';
import { TaskHeading } from '../tile/profile-task-tile/TaskHeading';
import { TaskTileError, TaskTileLoading } from '../tile/profile-task-tile/TaskTileFallbacks';
import { TaskTileFrame } from '../tile/profile-task-tile/TaskTileFrame';
import { TaskFieldSection, TaskMetaRowInfo, TaskWorkflowStepRow } from '../tile/TaskFieldPrimitives';
import { AutomationAiNotice, WorkflowStepScore } from './AutomationAssessmentSection';
import {
    AutomationDetailsBox,
    type AutomationDetails as AutomationDetailsFields,
    complexityKeys,
} from './AutomationDetailsBox';
import { automationRecommendationTranslationKeys as keys } from './automation-assessment-translations';
import {
    DEFAULT_RECIPE_VIEW,
    downloadAutomationRecipe,
    RECIPE_VIEWS,
    type RecipeView,
    toAutomationRecipeFileName,
    toAutomationRecipeMarkdown,
    toRecipeView,
} from './automation-recipe-document';
import type { AutomationRecommendationTilePayload } from './automation-recommendation-tile-url';
import { toProfileContext } from './automation-tile-address';

const hasText = (value: string | undefined): value is string => value !== undefined && value.trim().length > 0;

const hasBuildPlan = (instructions: WorkTaskAutomation['instructions']): boolean =>
    instructions !== undefined &&
    (hasText(instructions.brief) ||
        instructions.trigger !== undefined ||
        instructions.steps.length > 0 ||
        instructions.constraints.length > 0);

// Tabs panels pad themselves; the recipe frame brings its own spacing.
const RECIPE_TAB_PANEL_CLASS = '!p-0';

const recipeViewKeys = {
    preview: keys.recipePreview,
    source: keys.recipeSource,
} as const satisfies Record<RecipeView, string>;

/**
 * The recipe as the English agent definition it is meant to become: a file to install, downloaded
 * under the name it shows, with its markdown source, a rendered preview and a copy action behind a
 * disclosure.
 */
const RecipeDocument: FunctionComponent<{ readonly automation: BuildPlanAutomation }> = ({ automation }) => {
    const translate = useTranslate();
    const copyTextToClipboard = useCopyTextToClipboard();
    const [open, setOpen] = useState(false);
    const [view, setView] = useState<RecipeView>(DEFAULT_RECIPE_VIEW);
    const markdown = toAutomationRecipeMarkdown(automation);
    const fileName = toAutomationRecipeFileName(automation.title);

    return (
        <Collapsible open={open} onOpenChange={setOpen}>
            <section
                className="flex flex-col overflow-hidden rounded-xl bg-surface-hover p-xxs shadow-sm"
                data-testid="automation-recommendation-recipe"
            >
                <div className="flex items-center gap-l rounded-lg bg-surface-raised p-l shadow-sm">
                    <span className="min-w-0 flex-1 truncate font-mono text-body-xs text-text-secondary" lang="en">
                        {fileName}
                    </span>
                    <span className="flex shrink-0 items-center gap-xs">
                        <Button
                            data-testid="automation-recommendation-recipe-download"
                            iconPosition="leading"
                            size="sm"
                            type="button"
                            variant="primary"
                            onClick={() => downloadAutomationRecipe({ fileName, markdown })}
                        >
                            <IconFileDownload size={16} />
                            {translate(keys.download)}
                        </Button>
                    </span>
                </div>
                <Collapsible.Trigger asChild={true}>
                    <Button
                        className="flex items-center gap-xs rounded-lg border-0 bg-transparent px-m py-s text-left text-body-xs text-text-secondary hover:text-text-primary"
                        data-testid="automation-recommendation-recipe-toggle"
                        type="button"
                        variant="plain"
                    >
                        <IconChevronRightSmall
                            aria-hidden={true}
                            className="shrink-0 transition-transform duration-200 ease-out motion-reduce:transition-none"
                            size={16}
                            style={{ transform: open ? 'rotate(90deg)' : undefined }}
                        />
                        {translate(open ? keys.hideBuildPlan : keys.showBuildPlan)}
                    </Button>
                </Collapsible.Trigger>
                <Collapsible.Content>
                    <Tabs value={view} onValueChange={(value) => setView(toRecipeView(value))}>
                        <div className="flex flex-col gap-xxs">
                            <div className="flex items-center justify-between gap-s pr-xs">
                                <TabsList
                                    aria-label={translate(keys.recipeView)}
                                    className="!ml-m !w-fit !rounded-lg !p-xxs"
                                >
                                    {RECIPE_VIEWS.map((option) => (
                                        <TabsTrigger
                                            key={option}
                                            className="!px-s !py-xxs !text-body-xs data-[state=active]:bg-surface-raised data-[state=active]:shadow-sm"
                                            value={option}
                                        >
                                            {translate(recipeViewKeys[option])}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>
                                <Button
                                    aria-label={translate(keys.copy)}
                                    data-testid="automation-recommendation-recipe-copy"
                                    iconOnly={true}
                                    size="sm"
                                    type="button"
                                    variant="tertiary"
                                    onClick={() => void copyTextToClipboard(markdown)}
                                >
                                    <IconCopy size={16} />
                                </Button>
                            </div>
                            <TabsContent className={RECIPE_TAB_PANEL_CLASS} lang="en" value="source">
                                <div className="rounded-lg bg-surface-raised p-xs shadow-sm">
                                    <pre className="m-0 shrink-0 overflow-x-auto whitespace-pre-wrap rounded-sm bg-surface-canvas p-s font-mono text-[12px] leading-5 text-text-primary">
                                        <code>{markdown}</code>
                                    </pre>
                                </div>
                            </TabsContent>
                            <TabsContent className={RECIPE_TAB_PANEL_CLASS} lang="en" value="preview">
                                <div className="rounded-lg bg-surface-raised px-l py-m text-xs leading-normal text-text-secondary shadow-sm">
                                    <ChatMarkdown>{markdown}</ChatMarkdown>
                                </div>
                            </TabsContent>
                        </div>
                    </Tabs>
                </Collapsible.Content>
            </section>
        </Collapsible>
    );
};

/** The workflows a recommended automation covers, with how much of each it automates. */
export const AutomationWorkflowRows: FunctionComponent<{ readonly workflows: WorkTaskAutomation['workflows'] }> = ({
    workflows,
}) => {
    const translate = useTranslate();
    const workflowLabel = translate(profileTaskTranslationKeys.workflow);

    return (
        <TaskFieldSection title={workflowLabel}>
            <ol className="m-0 flex list-none flex-col gap-s p-0">
                {workflows.map((workflow, position) => (
                    <li key={workflow.workflowIndex ?? position} className="list-none">
                        <TaskWorkflowStepRow
                            className="flex gap-m rounded-xl bg-surface-subtle px-xl py-l"
                            description={[
                                workflow.complexityLevel === undefined
                                    ? undefined
                                    : `${translate(keys.complexity)}: ${translate(complexityKeys[workflow.complexityLevel])}`,
                                workflow.autonomyLevel === undefined
                                    ? undefined
                                    : `${translate(keys.autonomy)}: ${translate(keys.autonomyValue, { value: workflow.autonomyLevel })}`,
                            ]
                                .filter((part) => part !== undefined)
                                .join(' · ')}
                            title={workflowLabel}
                            trailing={
                                <span className="flex shrink-0 items-start gap-xs self-start">
                                    <WorkflowStepScore
                                        fraction={
                                            workflow.automatablePercentage === undefined
                                                ? undefined
                                                : workflow.automatablePercentage / 100
                                        }
                                        showChevron={false}
                                        isPending={false}
                                    />
                                    {hasText(workflow.reasoning) ? (
                                        <TaskMetaRowInfo label={workflowLabel} text={workflow.reasoning} tone="faint" />
                                    ) : null}
                                </span>
                            }
                        />
                    </li>
                ))}
            </ol>
        </TaskFieldSection>
    );
};

type BuildPlanAutomation = Pick<WorkTaskAutomation, 'instructions' | 'title'>;

/** A recommended automation's build plan as a markdown document; nothing when it has none. */
export const AutomationBuildPlan: FunctionComponent<{ readonly automation: BuildPlanAutomation }> = ({
    automation,
}) => {
    const translate = useTranslate();

    return hasBuildPlan(automation.instructions) ? (
        <TaskFieldSection title={translate(keys.buildPlan)}>
            <RecipeDocument automation={automation} />
        </TaskFieldSection>
    ) : null;
};

type AutomationContent = {
    readonly buildPlan: BuildPlanAutomation;
    /** Shown between the description and the Details, such as where the automation is recommended. */
    readonly children?: ReactNode;
    readonly description?: string | undefined;
    readonly details: AutomationDetailsFields | undefined;
    /** Placeholders where the description goes while it is still read. */
    readonly isDescriptionLoading?: boolean;
    readonly workflows: WorkTaskAutomation['workflows'];
};

const AutomationDetails: FunctionComponent<
    AutomationContent & {
        /**
         * Inside another tile, below a heading that already says what the automation does: no
         * description, tighter spacing, and no AI notice, which the tile's recommendations already give.
         */
        readonly inline?: boolean;
    }
> = ({ buildPlan, children, description, details, inline = false, isDescriptionLoading = false, workflows }) => (
    <div
        className={inline ? 'flex flex-col gap-xxl' : 'flex flex-col gap-6xl'}
        data-testid="automation-recommendation-details"
    >
        {inline ? null : isDescriptionLoading ? (
            <div className="flex flex-col gap-xs">
                <SkeletonBase className="h-3.5 w-full" />
                <SkeletonBase className="h-3.5 w-full" />
                <SkeletonBase className="h-3.5 w-3/5" />
            </div>
        ) : hasText(description) ? (
            <p className="m-0 text-xs leading-normal text-text-secondary">{description}</p>
        ) : null}
        {children}
        {details === undefined ? null : (
            <AutomationDetailsBox details={details} testId="automation-recommendation-meta" />
        )}
        {workflows.length === 0 ? null : <AutomationWorkflowRows workflows={workflows} />}
        <AutomationBuildPlan automation={buildPlan} />
        {inline ? null : <AutomationAiNotice />}
    </div>
);

const toAutomationContent = (automation: WorkTaskAutomation): AutomationContent => ({
    buildPlan: automation,
    description: automation.description,
    details: automation,
    workflows: automation.workflows,
});

/**
 * The tile every automation reads in, whether a task's recommendation or a catalogue agent: its
 * heading and short definition, then description, Details, workflows, build plan and the AI notice.
 */
export const AutomationTile: FunctionComponent<
    AutomationContent & {
        readonly breadcrumbs?: ReactNode;
        readonly onClose: () => void;
        readonly shortDefinition?: string | undefined;
        readonly testId?: string;
        readonly title: string;
    }
> = ({ breadcrumbs, onClose, shortDefinition, testId, title, ...content }) => (
    <TaskTileFrame testId={testId} onClose={onClose}>
        <div className="flex flex-col gap-m">
            {breadcrumbs}
            <TaskHeading title={title} />
            {hasText(shortDefinition) ? (
                <p className="m-0 text-body-xs text-text-secondary">{shortDefinition}</p>
            ) : null}
        </div>
        <AutomationDetails {...content} />
    </TaskTileFrame>
);

/**
 * A recommended automation's details inline, read like its tile reads them, for a page showing it
 * within another tile below the automation's own heading. Nothing while it loads, without the
 * recipes flag, or once it is gone.
 */
export const InlineAutomationRecommendationDetails: FunctionComponent<{
    readonly automationId: string;
    readonly taskId: string;
}> = ({ automationId, taskId }) => {
    const query = useWorkTaskAutomationRecommendation({ state: 'succeeded', taskId });
    const automation = findRecommendedAutomation(query.data, automationId);

    return automation === undefined ? null : <AutomationDetails {...toAutomationContent(automation)} inline={true} />;
};

export const AutomationRecommendationTile: FunctionComponent<{
    readonly onClose: () => void;
    readonly payload: AutomationRecommendationTilePayload;
}> = ({ onClose, payload }) => {
    const translate = useTranslate();
    useUrlTileEscapeClose(onClose);
    // Opened from a succeeded recommendation, or deep-linked to one; reads the task section's cache.
    const isEnabled = useAutomationRecommendationsEnabled();
    const query = useWorkTaskAutomationRecommendation({ state: 'succeeded', taskId: payload.taskId });

    // A deep link stays parseable without the flag, but finds nothing to show.
    if (!isEnabled) {
        return (
            <TaskTileFrame onClose={onClose}>
                <TaskTileError />
            </TaskTileFrame>
        );
    }

    if (query.data === undefined && !query.isError) {
        return (
            <TaskTileFrame onClose={onClose}>
                <TaskTileLoading />
            </TaskTileFrame>
        );
    }

    const automation = findRecommendedAutomation(query.data, payload.automationId);

    if (automation === undefined) {
        return (
            <TaskTileFrame onClose={onClose}>
                <TaskTileError />
            </TaskTileFrame>
        );
    }

    const context = toProfileContext(payload);

    return (
        <AutomationTile
            {...toAutomationContent(automation)}
            breadcrumbs={
                context === undefined ? undefined : (
                    <ProfileTaskBreadcrumbs context={context} currentItemTitle={translate(keys.title)} />
                )
            }
            shortDefinition={automation.shortDefinition}
            testId="automation-recommendation-tile"
            title={automation.title ?? translate(keys.title)}
            onClose={onClose}
        >
            {/* Recommended automations are catalogue agents; without a profile there is no architecture to place one in. */}
            {context === undefined ? null : (
                <AgentAttributions
                    agentId={automation.automationId}
                    scope={{ architectureId: context.architectureId, kind: 'job' }}
                />
            )}
        </AutomationTile>
    );
};
